import { Component, OnInit, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { lastValueFrom, map } from 'rxjs';
import {
  Campaign,
  CampaignControllerService,
  CampaignEligibleCustomerDTO,
  CampaignGift,
  CampaignProductValidationDTO
} from 'src/app/admin-api';
import { TableColumn } from 'src/app/components/table';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';

@Component({
  selector: 'app-campaign-generate',
  templateUrl: './campaign-generate.component.html',
  styleUrl: './campaign-generate.component.scss',
  encapsulation: ViewEncapsulation.None,
  providers: [MessageService, ConfirmationService]
})
export class CampaignGenerateComponent implements OnInit {
  private campaignService = inject(CampaignControllerService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private title = inject(Title);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);

  private eligibleCustomersSignal = signal<CampaignEligibleCustomerDTO[]>([]);
  private searchValue = signal<string>('');

  filterForm = new FormGroup({
    search: new FormControl<string>(''),
    strategy: new FormControl<string>('AUTO')
  });

  readonly strategyOptions = [
    { label: 'Automático (Prioriza Edição se Nulo, senão Avulso)', value: 'AUTO' },
    { label: 'Forçar avulso', value: 'FORCE_AVULSO' }
  ];

  readonly spinnerStyle = { width: '20px', height: '20px' };

  campaignId: number | null = null;
  campaign: Campaign | null = null;
  gifts: Array<CampaignGift & { validation?: CampaignProductValidationDTO }> = [];
  editionId?: number;
  stockValidationErrors: string[] = [];
  canGenerate = false;
  canGenerateTooltip = '';
  statusLabel = '';
  statusBadgeClass = '';

  filteredCustomers = computed(() => {
    const term = this.searchValue().trim().toLowerCase();
    const all = this.eligibleCustomersSignal();
    if (!term) return all;
    return all.filter(
      (customer) =>
        customer.email?.toLowerCase().includes(term) ||
        String(customer.personId).includes(term) ||
        customer.signedPersonIds?.toLowerCase().includes(term)
    );
  });

  columns = [
    new TableColumn.Builder()
      .setHeader('ID')
      .setField('personId')
      .setFilter(false)
      .setType('text')
      .build(),
    new TableColumn.Builder()
      .setHeader('E-mail')
      .setField('email')
      .setFilter(false)
      .setType('text')
      .build(),
    new TableColumn.Builder()
      .setHeader('Qtd. Indicações')
      .setField('inviteCount')
      .setFilter(false)
      .setType('number')
      .build(),
    new TableColumn.Builder()
      .setHeader('Planos')
      .setField('planNames')
      .setFilter(false)
      .setType('text')
      .build(),
    new TableColumn.Builder()
      .setHeader('PersonID assinou')
      .setField('signedPersonIds')
      .setFilter(false)
      .setType('text')
      .build(),
    new TableColumn.Builder()
      .setHeader('Valor assinaturas')
      .setField('subscriptionValue')
      .setFilter(false)
      .setType('currency')
      .build()
  ];

  async ngOnInit(): Promise<void> {
    this.title.setTitle('Marketing - Gerar Brindes');
    const id = this.route.snapshot.paramMap.get('campaignId');
    if (id) {
      this.campaignId = Number(id);
      LoaderService.showLoader();
      try {
        await Promise.all([
          this.loadCampaign(),
          this.loadGifts(),
          this.loadEligibleCustomers(false)
        ]);
      } catch (error) {
        AppDialogService.showErrorDialog(error);
      } finally {
        LoaderService.showLoader(false);
      }
    }
  }

  updateSearch(): void {
    this.searchValue.set(this.filterForm.controls['search'].value ?? '');
  }

  async loadCampaign(): Promise<void> {
    try {
      this.campaign = await lastValueFrom(
        this.campaignService
          .getCampaign(this.campaignId)
          .pipe(map((r) => r.result))
      );
      this.statusLabel = this.mapStatusLabel(this.campaign?.status);
      this.statusBadgeClass = this.mapStatusBadgeClass(this.campaign?.status);
      this.updateCanGenerate();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
      throw error;
    }
  }

  async loadGifts(): Promise<void> {
    if (!this.campaignId) return;
    try {
      const gifts = await lastValueFrom(
        this.campaignService.listGifts(this.campaignId).pipe(map((r) => r.result))
      );
      this.gifts = gifts.map((g) => ({ ...g, _validating: !!g.internalEan }));
      await Promise.all(
        gifts.map(async (gift, i) => {
          if (!gift.internalEan) return;
          try {
            const validation = await lastValueFrom(
              this.campaignService
                .validateProduct({ ean: gift.internalEan })
                .pipe(map((r) => r.result))
            );
            this.gifts = this.gifts.map((g, idx) =>
              idx === i ? { ...g, validation, _validating: false } : g
            );
          } catch (error) {
            this.gifts = this.gifts.map((g, idx) =>
              idx === i ? { ...g, _validating: false } : g
            );
            AppDialogService.showErrorDialog(error);
          }
        })
      );
    } catch (error) {
      AppDialogService.showErrorDialog(error);
      throw error;
    }
  }

  async loadEligibleCustomers(showLoader = true): Promise<void> {
    if (!this.campaignId) return;
    if (showLoader) LoaderService.showLoader();
    try {
      this.eligibleCustomersSignal.set(
        await lastValueFrom(
          this.campaignService
            .listEligibleCustomers(this.campaignId)
            .pipe(map((r) => r.result))
        )
      );
    } catch (error) {
      AppDialogService.showErrorDialog(error);
      throw error;
    } finally {
      if (showLoader) LoaderService.showLoader(false);
    }
  }

  validateGiftStock(): boolean {
    this.stockValidationErrors = [];
    const totalCustomers = this.eligibleCustomersSignal().length;

    for (const gift of this.gifts) {
      const stock = gift.validation?.inStockReserveMarketing || 0;
      const quantity = gift.quantity || 1;
      const requiredStock = totalCustomers * quantity;
      const giftName =
        gift?.validation?.productVariantName ||
        gift.internalEan ||
        'Desconhecido';

      if (stock === 0) {
        this.stockValidationErrors.push(
          `O brinde "${giftName}" (EAN: ${gift.internalEan}) possui estoque zerado.`
        );
      } else if (stock < requiredStock) {
        this.stockValidationErrors.push(
          `O brinde "${giftName}" (EAN: ${gift.internalEan}) possui estoque insuficiente: ${stock} unidades disponíveis para ${totalCustomers} clientes (necessário: ${requiredStock} unidades).`
        );
      }
    }

    return this.stockValidationErrors.length === 0;
  }

  generateGifts(event: Event): void {
    if (!this.campaignId) return;

    if (!this.validateGiftStock()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Estoque insuficiente',
        detail:
          'A geração foi bloqueada. Verifique os erros de estoque abaixo.',
        life: 6000
      });
      return;
    }

    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'Tem certeza que deseja gerar os brindes? Esta ação é irreversível.',
      accept: async () => {
        LoaderService.showLoader();
        try {
          await lastValueFrom(
            this.campaignService.generateCampaign(
              {
                sendStrategy: this.filterForm.controls['strategy'].value ?? 'AUTO',
                editionId: this.editionId
              },
              this.campaignId
            )
          );
          await this.loadEligibleCustomers(false);
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Brindes gerados com sucesso.'
          });
          await this.router.navigate(['/marketing/campaigns']);
        } catch (error) {
          AppDialogService.showErrorDialog(error);
        } finally {
          LoaderService.showLoader(false);
        }
      }
    });
  }

  trackGift(_index: number, gift: any): string {
    return gift.internalEan ?? String(_index);
  }

  navigateToEdit(): void {
    if (this.campaignId) {
      this.router.navigate(['/marketing/campaigns', this.campaignId]);
    }
  }

  private mapStatusBadgeClass(status?: string): string {
    switch ((status || '').toUpperCase()) {
      case 'ACTIVE': return 'status--active';
      case 'PLANNING':
      case 'DRAFT': return 'status--progress';
      case 'FINISHED': return 'status--warning';
      case 'CANCELLED': return 'status--danger';
      default: return '';
    }
  }

  private mapStatusLabel(status?: string): string {
    switch ((status || '').toUpperCase()) {
      case 'DRAFT': return 'Rascunho';
      case 'PLANNING': return 'Planejamento';
      case 'ACTIVE': return 'Ativa';
      case 'FINISHED': return 'Finalizada';
      case 'CANCELLED': return 'Cancelada';
      default: return status || '-';
    }
  }

  private updateCanGenerate(): void {
    if (!this.campaign?.endDate) {
      this.canGenerate = false;
      this.canGenerateTooltip =
        'Brindes só podem ser gerados após o término da campanha';
      return;
    }
    const endDate = new Date(this.campaign.endDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    this.canGenerate = endDate < today;
    this.canGenerateTooltip = this.canGenerate
      ? ''
      : 'Brindes só podem ser gerados após o término da campanha';
  }
}
